import type { AppProps } from 'next/app';
import type { PostHog } from 'posthog-js';
import '@/styles/globals.css';
import '../components/registration';
import '@/mocks';
import { ColorProvider } from '@/utils/ColorProvider';
import SentryErrorBoundary from '@/components/SentryErrorBoundary';
import posthog from '@/lib/posthogWrapper';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { PostHogProvider } from '@/lib/posthogProvider';
import { getSubdomain } from '@/utils/getDomain';
import { getTrackingParams } from '@/utils/analytics';
import { readBootstrappedFlags } from '@/lib/cookies';
import { FLAG_TESTER } from '@/constants/featureFlags';
import { getFlagTesterValue } from '@/utils/flagTester';
import { buildBootstrap } from '@/lib/posthogBootstrap';
import { recordSplitUrlExposures } from '@/utils/splitUrlExposure';
import { fontFamilyVariables } from '@/lib/fonts';
if (typeof window !== 'undefined' && !process.env.CI) {
  const urlParams = new URLSearchParams(window.location.search);
  const flagTesterValue = getFlagTesterValue(urlParams);

  // Read bootstrapped flags from cookies set by middleware (runs before React)
  const bootstrappedFlags = readBootstrappedFlags() ?? {};
  const bootstrap = buildBootstrap(bootstrappedFlags, urlParams, navigator.userAgent);
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    bootstrap,
    // Object form on purpose: a literal `false` would also drop network timing
    // from session replays.
    capture_performance: {
      web_vitals: false
    },
    // App below sends $pageview itself, after the page has registered its session properties, so
    // the SDK's own one at init would be a duplicate without them. $pageleave on tab close is
    // tied to capture_pageview by default, so it is kept on explicitly.
    capture_pageview: false,
    capture_pageleave: true,
    // Session recording is left at the posthog-js default (autostart at init) so whether a
    // session records is decided entirely by the project's Session Replay settings in PostHog
    // (opt-in, sampling, URL triggers/blocklist) rather than by app code. This reverses PR #610,
    // which delayed recording until Anura returned a result.
    loaded: (ph: PostHog) => {
      ph.register({
        environment: process.env.NEXT_PUBLIC_ENVIRONMENT || 'development',
        url: window.location.pathname,
        project_name: process.env.NEXT_PUBLIC_PROJECT_NAME as string || 'Unknown / Development',
        domain: getSubdomain(),
        ...getTrackingParams(),
        EFID: urlParams.get('efid') || '',
        OID: urlParams.get('oid') || '',
        AID: urlParams.get('aid') || '',
        ef_offer_id: urlParams.get('ef_offer_id') || '',
        ef_affiliate_id: urlParams.get('ef_affiliate_id') || '',
        cid: urlParams.get('cid') || ''
      });
      if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'development') {
        ph.debug();
      }

      // ?flag_tester=<flag-keys> QA override — must match the Edge leg in lib/posthogServerFlags.ts.
      if (flagTesterValue !== null) {
        ph.setPersonPropertiesForFlags({
          [FLAG_TESTER]: flagTesterValue
        }, false);
      } else {
        // The raw instance has no top-level unset — go through the wrapper (featureFlags ext.)
        posthog.unsetPersonPropertiesForFlags([FLAG_TESTER], false);
      }

      // Reload feature flags so subsequent reads reflect the stored flag overrides
      // (flag_tester above) instead of only the initial middleware bootstrap values
      posthog.reloadFeatureFlags();
    }
  });

  // At module load, the same moment a microfrontends child records its arm, so both sides of a split-url
  // test count visitors who leave before React hydrates.
  recordSplitUrlExposures();
}
export default function App({
  Component,
  pageProps
}: AppProps) {
  const router = useRouter();
  useEffect(() => {
    if (!router.isReady) return;

    // Track initial page load
    posthog.capture('$pageview', {
      path: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // Track route changes
    const handleRouteChange = (url: string) => {
      posthog.capture('$pageview', {
        path: url,
        referrer: document.referrer,
        search: router.query,
        title: document.title
      });
    };

    // Track when user starts to leave a page
    const handleRouteStart = () => {
      posthog.capture('$pageleave', {
        path: router.asPath
      });
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('routeChangeStart', handleRouteStart);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('routeChangeStart', handleRouteStart);
    };
  }, [router.isReady]);
  return <>
			{/*
    Publishes the `--font-*` vars globals.css reads, carrying the `X Fallback` metric
    families. Outside the error boundary because its fallback replaces children, which
    would drop these and leave the error screen on unadjusted fallback metrics. Plain
    <style>, not styled-jsx — styled-jsx manages its CSS through a runtime that re-inserts
    it during hydration, and for that frame the vars are missing, so text visibly re-resolves.
    */}
			<Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
				<style>{fontFamilyVariables}</style>
			</Head>
			<SentryErrorBoundary data-sentry-element="SentryErrorBoundary" data-sentry-source-file="_app.tsx">
				<PostHogProvider client={posthog} data-sentry-element="PostHogProvider" data-sentry-source-file="_app.tsx">
					<ColorProvider data-sentry-element="ColorProvider" data-sentry-source-file="_app.tsx">
						<Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
						<div style={{
            display: 'none'
          }} id="hidden-form-data"></div>
					</ColorProvider>
				</PostHogProvider>
			</SentryErrorBoundary>
		</>;
}